import type { PropsWithChildren, ReactElement } from 'react';
import { createContext, useContext, useLayoutEffect } from 'react';
import type { AppUrlParams, EventGallery, SearchResultsOverview } from '../types';
import { useQuery } from '@apollo/client';
import type { GalleryQueryResponseData, GalleryQueryVariables } from '../views/gallery/queries';
import { GALLERY_QUERY } from '../views/gallery/queries';
import { useEvent } from '@fastnorth/react-results-event-components';
import { useParams, useSearchParams } from 'react-router-dom';
import { injectBrandingColors } from '../helpers';
import Loading from '../views/loading/Loading';
import type {
  SearchResultOverviewQueryVariables,
  SearchResultOverviewResponseData,
} from '../views/gallery/components/search/queries';
import { SEARCH_OVERVIEW_QUERY } from '../views/gallery/components/search/queries';
import type { EventQueryResponseData, EventQueryVariables } from '../views/gallery/queries/event';
import { EVENT_QUERY } from '../views/gallery/queries/event';

export type GalleryProviderCtx = {
  loading: boolean;
  error?: Error;
  gallery?: EventGallery;
  searchResults?: SearchResultsOverview;
};

const defaultEventProvider: GalleryProviderCtx = {
  loading: false,
  gallery: undefined,
  error: undefined,
  searchResults: undefined,
};

const GalleryCtx = createContext<GalleryProviderCtx>(defaultEventProvider);

const GalleryProvider = ({ children }: PropsWithChildren): ReactElement => {
  const { galleryId } = useParams<AppUrlParams>() as AppUrlParams;
  const [searchParams] = useSearchParams({ tags: '' });
  const { setEvent } = useEvent();
  const rawTags = searchParams.get('tags') || '';

  const {
    data: responseData,
    loading,
    error,
  } = useQuery<GalleryQueryResponseData, GalleryQueryVariables>(GALLERY_QUERY, {
    variables: { galleryId: galleryId },
    fetchPolicy: 'cache-and-network',
    skip: !galleryId, // only run this query if the galleryId is present in the url
  });

  const eventId = responseData?.gallery?.event?.raceRosterEventId;

  const {
    data: eventResponseData,
    loading: eventLoading,
    error: eventError,
  } = useQuery<EventQueryResponseData, EventQueryVariables>(EVENT_QUERY, {
    // @ts-expect-error this query is skipped when eventId isn't set
    variables: { eventId },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !eventId, // only run this query if the galleryId is present in the url
  });

  const {
    data: searchData,
    loading: searching,
    error: searchError,
  } = useQuery<SearchResultOverviewResponseData, SearchResultOverviewQueryVariables>(SEARCH_OVERVIEW_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { tags: rawTags, galleryId: galleryId as string, pageNumber: 1 },
    skip: !responseData || !rawTags, // only run this query if we have gallery data and searchParams and rawTags
  });

  useLayoutEffect(() => {
    if (eventResponseData?.event) {
      setEvent(eventResponseData.event);
      injectBrandingColors(eventResponseData.event.branding);
    }
  }, [eventResponseData, setEvent]);

  const gallery = responseData?.gallery;
  const searchResults = searchData?.searchOverview;

  return (
    <GalleryCtx.Provider
      value={{
        gallery,
        searchResults,
        loading: loading || eventLoading || searching,
        error: error || eventError || searchError,
      }}>
      <Loading
        className={'vh-100'}
        busy={(!gallery && loading) || (!searchResults && searching) || (!eventResponseData?.event && eventLoading)}>
        {children}
      </Loading>
    </GalleryCtx.Provider>
  );
};

export const useGallery = (): GalleryProviderCtx => useContext<GalleryProviderCtx>(GalleryCtx);

export default GalleryProvider;
